import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Project } from 'src/app/model/project/project';
import { SubProject } from 'src/app/model/project/sub-project';
import { BaseProject } from 'src/app/model/project/base-project';
import { MatStepper } from '@angular/material/stepper';
import PlacementInvoice from 'src/app/model/invoice/placement-invoice';
import Product from 'src/app/model/product/product';
import { Pickup } from 'src/app/model/project/pickup';
import { SnackService } from 'src/app/services/snack.service';
import { ContactPickerEvent } from 'src/app/shared/contact-picker/contact-picker-event';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import Contact from 'src/app/model/Contact/contact';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ProductTypes } from 'src/app/model/product/product-type';
import Board from 'src/app/model/product/board/board';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import {
  AbstractControl,
  UntypedFormControl,
  ValidationErrors,
} from '@angular/forms';
import ProductHelper from 'src/app/helpers/product-helper';
import { CreateViewBaseProjectHelper } from './create-view-base-project-helper';
import DateHelper from 'src/app/helpers/date-helper';
import { combineLatest, Subject } from 'rxjs';
import ProductAsset from 'src/app/model/product-asset';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { StringHelper } from 'src/app/helpers/string-helper';
import IPBoardPlacement from 'src/app/model/product/ip-board/ip-board-placement';
import { STATUS } from '../../../../model/project/status';

@Component({
  selector: 'create-view-base-project',
  templateUrl: './create-view-base-project.component.html',
  styleUrls: ['./create-view-base-project.component.scss'],
})
export class CreateViewBaseProjectComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, AfterViewInit
{
  private static QUOTATION_SUBMIT_LABEL = 'Offerte aanvragen';
  private static STANDARD_PRICE_SUBMIT_LABEL = 'Bestellen';
  private static IP_BOARD_SUBMIT_LABEL = 'Bestelling plaatsen';
  private static VALUE_TRUE: (c: AbstractControl) => ValidationErrors = (
    c: AbstractControl
  ) => (c.value === true ? null : [{ key: '' }]);

  contactStepControl: UntypedFormControl;
  configurationStepControl: UntypedFormControl;
  designStepControl: UntypedFormControl;
  placementStepControl: UntypedFormControl;

  @ViewChild(MatStepper)
  matHorizontalStepper: MatStepper;

  @Input()
  project: Project;

  @Input()
  subProject: SubProject;

  @Input()
  submitProjectResult: Subject<any>;

  @Input()
  client: boolean = false;

  @Input()
  adminCreate: boolean = false;

  @Output()
  submitProject = new EventEmitter();

  @Output()
  save = new EventEmitter();

  @Output()
  updateOtherContacts = new EventEmitter();

  @Output()
  projectContactChange = new EventEmitter<ContactPickerEvent>();

  @ViewChild('projectStepper')
  stepper: MatStepper;

  baseProject: BaseProject;

  init = false;
  showAddProduct = true;
  hasValidAddress: boolean = false;
  hasValidStreet: boolean = false;
  submitLabel = CreateViewBaseProjectComponent.QUOTATION_SUBMIT_LABEL;

  otherContacts: Contact[];

  projectHasIPBoard: boolean = false;

  selectedStepperIndex: number;

  minPlacementDate: Date;

  projectPlacementAssets: ProductAsset[];

  higherFloorOptionSelected: boolean = false;

  constructor(
    private snackService: SnackService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.baseProject = <BaseProject>this.subProject;
    this.calculateProjectPlacementAssets();
    if (
      this.project.id == null ||
      this.subProject.status === STATUS.DRAFT.valueOf()
    ) {
      this.showAddProduct = false;
      this.initStepControls();
    } else if (!this.client) {
      this.initAdminViewStepControls();
    }
    if (this.baseProject.pickup == null) {
      this.baseProject.pickup = new Pickup();
    }
    if (this.baseProject.id != null && this.project.id != null && this.client) {
      this.baseProject.project = this.project;
    }
    this.minPlacementDate =
      DateHelper.getMomentBusinessDayMinProjectPlacement().toDate();
    this.calculateProjectHasIPBoard();

    const calculateSubmitLabelObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservableService.getProductAddedSubscribable(),
      this.projectDisplayObservableService.getProductPartDeletedSubscribable(),
      this.projectDisplayObservableService.getProductSizeChangedSubscribable()
    );
    this.addSubscription(calculateSubmitLabelObservable, () =>
      this.calculateSubmitLabel()
    );
    this.addSubscription(
      this.projectDisplayObservableService.getProductAddedSubscribable(),
      () => this.calculateProjectHasIPBoard()
    );
    this.addSubscription(
      this.projectDisplayObservableService.getProjectPlacementAssetsChangedSubscribable(),
      (id?: number) => {
        if (this.shouldRecalculateProjectPlacementAssets(id)) {
          this.calculateProjectPlacementAssets();
        }
      }
    );

    this.addSubscription(
      combineLatest([
        this.projectDisplayObservableService
          .getAddressWasHiddenSubscribable()
          .pipe(startWith(false), distinctUntilChanged()),
        this.projectDisplayObservableService
          .getValidAddressSubscribable()
          .pipe(startWith(false), distinctUntilChanged()),
        this.projectDisplayObservableService
          .getValidStreetSubscribable()
          .pipe(startWith(false), distinctUntilChanged()),
      ]),
      ([addressHidden, validAddress, validStreet]) => {
        if (!addressHidden && (!validAddress || !validStreet)) {
          this.placementStepControl.setValue(false);
        }
        this.hasValidStreet = addressHidden || validStreet;
        this.hasValidAddress = addressHidden || validAddress;
      }
    );
    this.init = true;
  }

  initStepControls(): void {
    this.doInitStepControls(false);
    this.selectedStepperIndex = 0;
  }

  initAdminViewStepControls(): void {
    this.doInitStepControls(true);
    this.selectedStepperIndex = 4;
  }

  ngAfterViewInit(): void {
    if (this.selectedStepperIndex > 0) {
      this.stepper.linear = false;
      this.stepper.selectedIndex = this.selectedStepperIndex;
      this.cd.detectChanges();
    }
  }

  private doInitStepControls(initialState: boolean): void {
    this.contactStepControl = new UntypedFormControl(
      initialState,
      CreateViewBaseProjectComponent.VALUE_TRUE
    );
    this.configurationStepControl = new UntypedFormControl(
      initialState,
      CreateViewBaseProjectComponent.VALUE_TRUE
    );
    this.designStepControl = new UntypedFormControl(
      initialState,
      CreateViewBaseProjectComponent.VALUE_TRUE
    );
    this.placementStepControl = new UntypedFormControl(
      initialState,
      CreateViewBaseProjectComponent.VALUE_TRUE
    );
  }

  private calculateProjectPlacementAssets(): void {
    this.projectPlacementAssets =
      this.baseProject.projectPlacement == null ||
      !CollectionHelper.isPopulated(
        this.baseProject.projectPlacement.projectPlacementAssets
      )
        ? []
        : this.baseProject.projectPlacement.projectPlacementAssets.filter(
            (pa) => pa != null && !pa.archived
          );
  }

  private shouldRecalculateProjectPlacementAssets(id?: number): boolean {
    return (
      this.baseProject.projectPlacement != null &&
      ((id == null && this.baseProject.projectPlacement.id == null) ||
        this.baseProject.projectPlacement.id === id)
    );
  }

  saveContacts(event: ContactPickerEvent) {
    if (this.project.id == null) {
      this.project.projectContacts = event.projectContacts;
      this.setPlacementInvoices();
      this.projectDisplayObservableService
        .getProjectContactsChangedSubscribable()
        .next();
    } else {
      this.projectContactChange.emit(event);
    }
  }

  contactStepNext() {
    const hasErroringExtraContact =
      CollectionHelper.isPopulated(this.project.projectExtraCompanyContacts) &&
      this.project.projectExtraCompanyContacts.some(
        (e) =>
          StringHelper.isEmptyOrWhiteSpace(e.company) ||
          StringHelper.isEmptyOrWhiteSpace(e.contact)
      );
    if (this.project.collaborative && hasErroringExtraContact) {
      this.snackService.showError(
        'Gelieve bedrijfsnaam en contactpersoon volledig in te vullen'
      );
      return;
    }
    this.finishContactStep();
  }

  setPlacementInvoices() {
    if (this.subProject.projectInvoices == null) {
      this.subProject.projectInvoices = [];
    }
    let companiesWithInvoices = new Set<number>();
    for (let invoice of this.subProject.projectInvoices) {
      if (invoice.type === 'PlacementInvoice') {
        companiesWithInvoices.add(invoice.company.id);
      }
    }
    for (let pc of this.project.projectContacts) {
      if (!companiesWithInvoices.has(pc.company.id)) {
        let placementInvoice = new PlacementInvoice(pc.company);
        placementInvoice.priceLines = [];
        placementInvoice.customProductInvoicePrices = [];
        this.subProject.projectInvoices.push(placementInvoice);
        companiesWithInvoices.add(pc.company.id);
      }
    }
  }

  addProduct() {
    this.showAddProduct = false;
    let prod = new Product();
    this.project.products.push(prod);
  }

  productInitialized(product: Product, index: number) {
    this.project.products[index] = product;
    this.showAddProduct = this.project.products.every(
      (p) => p.type != null && p.type !== ProductTypes.IP_BOARD.value
    );
    this.projectDisplayObservableService.getProductAddedSubscribable().next();
  }

  private finishContactStep(): void {
    this.contactStepControl.setValue(true);
    this.advanceStepper();
  }

  finishConfigurationStep(): void {
    this.configurationStepControl.setValue(true);
    if (this.client) {
      const error =
        CreateViewBaseProjectHelper.validateClientProductConfiguration(
          this.project.products
        );
      if (error != null) {
        this.snackService.showError(error);
        return;
      }
    }
    if (this.client) {
      this.projectDisplayObservableService
        .getSelectedOtherContactsChangedSubscribable()
        .next(this.otherContacts);
    }
    this.advanceStepper();
  }

  finishDesignStep(): void {
    this.designStepControl.setValue(true);
    this.advanceStepper();
  }

  finishPlacementStep(): void {
    this.placementStepControl.setValue(true);
    if (this.client) {
      const error = CreateViewBaseProjectHelper.validateProjectPlacement(
        this.baseProject,
        this.project,
        this.projectHasIPBoard,
        this.minPlacementDate,
        this.higherFloorOptionSelected
      );
      if (error != null) {
        this.placementStepControl.setValue(false);
        this.snackService.showError(error);
        return;
      }
      if (!this.hasValidAddress || !this.hasValidStreet) {
        this.placementStepControl.setValue(false);
        this.snackService.showError(
          !this.hasValidAddress
            ? 'Gelieve alle verplichte adres velden in te vullen'
            : 'Onbekende straat, maak gebruik van de autocomplete om een accuraat adres te creëren'
        );
        return;
      }
      const hasEmptyArrows = this.project.products
        .filter((x) => ProductHelper.isIPBoard(x))
        .map((p) => {
          const pp = p.productPlacement as IPBoardPlacement;
          let result = false;
          if (
            pp != null &&
            pp.arrows != null &&
            (!StringHelper.isEmptyOrWhiteSpace(pp.arrows.arrowsDesign) ||
              !StringHelper.isEmptyOrWhiteSpace(pp.arrows.arrowsLocation) ||
              pp.arrows.amountArrowsLeft > 0 ||
              pp.arrows.amountArrowsRight > 0)
          ) {
            result =
              pp.arrows.amountArrowsLeft > 0 || pp.arrows.amountArrowsRight > 0;
          } else if (
            pp != null &&
            pp.arrows != null &&
            pp.arrows.arrowsDesign === null &&
            pp.arrows.arrowsLocation === null &&
            pp.arrows.amountArrowsLeft === null &&
            pp.arrows.amountArrowsRight === null
          ) {
            result = true;
          }

          return result;
        })
        .some((b) => b === false);
      if (hasEmptyArrows) {
        this.placementStepControl.setValue(false);
        this.snackService.showError(
          'Gelieve een aantal pijlen in te geven of pijlen uit te vinken'
        );
        return;
      }
    }
    this.advanceStepper();
  }

  advanceStepper() {
    this.matHorizontalStepper.next();
    window.scrollTo(0, 0);
  }

  disallowProductStep() {
    return (
      this.project.products == null ||
      this.project.products.length === 0 ||
      this.project.products.some((p) => p.type == null)
    );
  }

  calculateSubmitLabel(): void {
    if (!CollectionHelper.isPopulated(this.project.products)) {
      this.submitLabel = CreateViewBaseProjectComponent.QUOTATION_SUBMIT_LABEL;
      return;
    }

    let label = CreateViewBaseProjectComponent.STANDARD_PRICE_SUBMIT_LABEL;

    for (const product of this.project.products) {
      if (product.type !== ProductTypes.BOARD.value) {
        label =
          product.type === ProductTypes.IP_BOARD.value
            ? CreateViewBaseProjectComponent.IP_BOARD_SUBMIT_LABEL
            : CreateViewBaseProjectComponent.QUOTATION_SUBMIT_LABEL;
        break;
      }
      const board = <Board>product;
      if (
        !CollectionHelper.isPopulated(board.boardParts) ||
        !board.boardParts.every((bp) => bp.signSize != null)
      ) {
        label = CreateViewBaseProjectComponent.QUOTATION_SUBMIT_LABEL;
        break;
      }
    }

    this.submitLabel = label;
  }

  emitUpdateOtherContacts(contacts: Contact[]): void {
    this.updateOtherContacts.emit(contacts);
    this.otherContacts = contacts;
    this.projectDisplayObservableService
      .getSelectedOtherContactsChangedSubscribable()
      .next(contacts);
  }

  private calculateProjectHasIPBoard(): void {
    this.projectHasIPBoard =
      CollectionHelper.isPopulated(this.project.products) &&
      this.project.products.some((p) => ProductHelper.isIPBoard(p));
  }

  protected readonly STATUS = STATUS;
}
